import { FunctionComponent, ReactNode } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "@/components";

export type DashboardBoxAccent = "green" | "indigo" | "yellow" | "red" | "purple" | "teal";

interface DashboardBoxProps {
	accent?: DashboardBoxAccent;
	icon?: ReactNode;
	title: ReactNode;
	subtitle?: ReactNode;
	headRight?: ReactNode;
	dismissible?: boolean;
	onDismiss?: () => void;
	children: ReactNode;
	footer?: ReactNode;
	className?: string;
}

const ACCENT_CLASSES: Record<DashboardBoxAccent, string> = {
	green: "bg-status-success-light text-status-success",
	indigo: "bg-action-200 text-brand-light",
	yellow: "bg-status-warning-light text-text-warning",
	red: "bg-status-error-light text-status-error",
	purple: "bg-purple-100 text-purple-800",
	teal: "bg-blue-lightest text-rb-turquoise",
};

export const DashboardBox: FunctionComponent<DashboardBoxProps> = ({
	accent = "indigo",
	icon,
	title,
	subtitle,
	headRight,
	dismissible = true,
	onDismiss,
	children,
	footer,
	className,
}) => {
	return (
		<div
			className={classNames(
				"bg-white border border-border-light rounded-xl overflow-hidden",
				className ?? ""
			)}
		>
			<div className="flex items-start gap-3 px-4 py-3 border-b border-divider">
				{icon && (
					<div
						className={classNames(
							"w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
							ACCENT_CLASSES[accent]
						)}
					>
						{icon}
					</div>
				)}
				<div className="flex-1 min-w-0">
					<div className="text-sm font-semibold text-text">{title}</div>
					{subtitle && <div className="text-xs text-text-meta mt-0.5">{subtitle}</div>}
				</div>
				{headRight}
				{dismissible && (
					<button
						aria-label="Dismiss"
						className="w-6 h-6 rounded-md text-text-meta hover:bg-background-light flex items-center justify-center shrink-0"
						type="button"
						onClick={onDismiss}
					>
						<Icon className="w-3 h-3" icon={faXmark} />
					</button>
				)}
			</div>
			<div className="divide-y divide-divider">{children}</div>
			{footer && (
				<div className="px-4 py-2 border-t border-divider text-xs text-center text-brand-light hover:bg-background-light">
					{footer}
				</div>
			)}
		</div>
	);
};
